import { useMemo } from 'react'
import useLoyaltyPointsEarnCalculator from './useLoyaltyPointsEarnCalculator'
import {
  generateLoyaltyPointsCalculatorPropertyOptions,
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'

interface Params {
  /**
   * Property rate to calculate status credits for
   */
  rate: App.PropertyRate | undefined
  /**
   * Country code of the property
   */
  propertyCountryCode: string | undefined
  /**
   * Current region code of the user
   */
  currentRegionCode: string
  /**
   * Whether the property is UltraLux
   */
  isUltraLux?: boolean
  /**
   * Number of nights for the booking
   */
  nights?: number
  /**
   * Skip the calculation
   */
  skip?: boolean
}

interface PropertyRateStatusCreditsResult {
  /**
   * Total status credits (base + bonus) that would be earned from booking this rate
   * including bundled flights if applicable
   */
  statusCredits: number
  /**
   * Whether the calculation is still loading
   */
  isLoading: boolean
  /**
   * Whether there was an error calculating
   */
  hasError: boolean
}

/**
 * Hook to calculate the status credits that would be earned from booking a property rate,
 * including bundled flight status credits if the rate includes flights.
 */
function usePropertyRateStatusCreditsCalculation(params: Params): PropertyRateStatusCreditsResult {
  const {
    rate,
    propertyCountryCode,
    currentRegionCode,
    isUltraLux = false,
    nights,
    skip = false,
  } = params

  const calculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (skip || !rate) {
      return [undefined, undefined]
    }

    return [
      generateLoyaltyPointsCalculatorPropertyOptions(rate, {
        propertyCountryCode,
        currentRegionCode,
        isUltraLux,
        nights,
      }),
      generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
    ]
  }, [skip, rate, propertyCountryCode, currentRegionCode, isUltraLux, nights])

  const { totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: skip || !rate,
  })

  const statusCredits = (totalPointsCalculation.data?.statusCredits ?? 0) + (totalPointsCalculation.data?.bonusStatusCredits ?? 0)
  const isLoading = !!totalPointsCalculation.initial || !!totalPointsCalculation.fetching
  const hasError = !!totalPointsCalculation.error

  return { statusCredits, isLoading, hasError }
}

export default usePropertyRateStatusCreditsCalculation
